import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, linkedSignal, output, signal } from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import {
  coinThrowToLine,
  cryptoRandom,
  linesFromHexagram,
  linesToHexagram,
  movingPositions,
  throwCoins,
  yarrowLine,
} from '../../core/iching-core';
import {
  IChingCastDistribution,
  IChingCastMode,
  IChingCoinFace,
  IChingLinePosition,
  IChingLineValue,
  IChingMethodStep,
} from '../../core/iching.models';
import { injectIChingTranslate } from '../iching-consult-translate';
import { HdIChingCastLedgerComponent } from './hd-iching-cast-ledger.component';
import { HdIChingCoinThrowComponent } from './hd-iching-coin-throw.component';
import { HdIChingHexagramMatrixComponent } from './hd-iching-hexagram-matrix.component';

type CoinFaces = [IChingCoinFace, IChingCoinFace, IChingCoinFace];

interface LastThrow {
  /** Aantal lijnen na deze worp; telt de worp niet meer mee, dan hoort hij niet meer bij de figuur. */
  readonly count: number;
  readonly faces?: CoinFaces;
}

interface ManualRow {
  readonly position: IChingLinePosition;
  readonly value: string;
  readonly options: readonly ESegmentedToggleOption[];
}

const LINE_VALUES: readonly IChingLineValue[] = [6, 7, 8, 9];
const POSITIONS: readonly IChingLinePosition[] = [1, 2, 3, 4, 5, 6];
/** Handmatige invoer van boven naar beneden: lijn 6 bovenaan, zoals in de figuur. */
const POSITIONS_TOP_DOWN: readonly IChingLinePosition[] = [6, 5, 4, 3, 2, 1];

/**
 * Fase 2 van de raadpleging: de worp. Drie manieren, gekozen met de
 * modustoggle bovenaan:
 *
 * - **coins**: virtuele munten, lijn per lijn van onder naar boven; in deze
 *   modus kiest een tweede toggle de kansverdeling (munten of duizendblad).
 * - **manual**: zelf echte munten gegooid; per lijn 6/7/8/9 invullen.
 * - **direct**: een hexagram uit de sleuteltabel of op nummer, plus bewegende lijnen.
 *
 * Controlled: de lijnen komen binnen via `lines`, elke wijziging gaat als
 * volledige nieuwe array naar buiten via `linesChange`.
 */
@Component({
  selector: 'hd-iching-cast-step',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ESegmentedToggleComponent,
    HdIChingCastLedgerComponent,
    HdIChingCoinThrowComponent,
    HdIChingHexagramMatrixComponent,
  ],
  templateUrl: './hd-iching-cast-step.component.html',
  styleUrl: './hd-iching-cast-step.component.scss',
})
export class HdIChingCastStepComponent {
  readonly mode = input.required<IChingCastMode>();
  readonly distribution = input.required<IChingCastDistribution>();
  readonly lines = input.required<readonly IChingLineValue[]>();
  readonly legend = input<IChingMethodStep['legend']>(undefined);
  /** Samenvattende weergave zodra de duiding er is: kleinere figuur. */
  readonly compact = input(false, { transform: booleanAttribute });
  /** Half ingevulde handmatige worp uit het concept (index 0 = lijn 1); wint zolang de worp onvolledig is. */
  readonly manualDraft = input<readonly (IChingLineValue | undefined)[] | undefined>(undefined);
  /** Muntzijden per worp (index 0 = lijn 1) voor het worp-overzicht; leeg bij handmatig, duizendblad of direct. */
  readonly coinFaces = input<ReadonlyArray<readonly IChingCoinFace[]>>([]);

  readonly linesChange = output<IChingLineValue[]>();
  readonly coinFacesChange = output<CoinFaces>();
  readonly modeChange = output<IChingCastMode>();
  readonly distributionChange = output<IChingCastDistribution>();
  /** Elke keuze in de handmatige modus, ook een onvolledige, zodat het concept ze bewaart. */
  readonly manualDraftChange = output<(IChingLineValue | undefined)[]>();

  /** Toevalsbron; publiek zodat specs een vaste reeks kunnen inzetten. */
  rng: () => number = cryptoRandom;

  protected readonly t = injectIChingTranslate();
  protected readonly positions = POSITIONS;

  protected readonly modeOptions = computed<ESegmentedToggleOption<IChingCastMode>[]>(() => [
    { value: 'coins', label: this.t('humanDesign.iching.cast.modeCoins') },
    { value: 'manual', label: this.t('humanDesign.iching.cast.modeManual') },
    { value: 'direct', label: this.t('humanDesign.iching.cast.modeDirect') },
  ]);

  protected readonly distributionOptions = computed<ESegmentedToggleOption<IChingCastDistribution>[]>(() => [
    { value: 'coins', label: this.t('humanDesign.iching.cast.distributionCoins') },
    { value: 'yarrow', label: this.t('humanDesign.iching.cast.distributionYarrow') },
  ]);

  protected readonly legendValues = computed(() => {
    const values = this.legend()?.values;
    return values === undefined ? [] : LINE_VALUES.map(v => ({ value: v, text: values[`${v}`] }));
  });

  protected readonly complete = computed(() => this.lines().length >= 6);

  // ── Munten ─────────────────────────────────────────────────────────────

  private readonly lastThrow = signal<LastThrow | undefined>(undefined);

  /** Munten van de laatste worp, alleen zolang die worp nog de bovenste lijn is. */
  protected readonly lastFaces = computed(() => {
    const last = this.lastThrow();
    return last !== undefined && last.count === this.lines().length ? last.faces : undefined;
  });

  /** Uitleg bij de bovenste lijn tot nu toe (uit de legende). */
  protected readonly lastValueText = computed(() => {
    const lines = this.lines();
    const value = lines[lines.length - 1];
    if (value === undefined) {
      return undefined;
    }
    const meaning = this.legend()?.values[`${value}`];
    return meaning ? `${value}: ${meaning}` : `${value}`;
  });

  protected throwLine(): void {
    if (this.complete()) {
      return;
    }
    let faces: CoinFaces | undefined;
    let value: IChingLineValue;
    if (this.distribution() === 'yarrow') {
      value = yarrowLine(this.rng);
    } else {
      faces = throwCoins(this.rng);
      value = coinThrowToLine(faces);
    }
    const next = [...this.lines(), value];
    this.lastThrow.set({ count: next.length, faces });
    this.linesChange.emit(next);
    if (faces !== undefined) {
      this.coinFacesChange.emit(faces);
    }
  }

  // ── Zelf gegooid ───────────────────────────────────────────────────────

  /**
   * Ingevulde waarden per lijn (index 0 = lijn 1): een volledige worp uit de lijnen, anders de
   * bewaarde halve handmatige worp, anders de lijnen tot nu toe.
   */
  private readonly manualValues = linkedSignal<readonly (IChingLineValue | undefined)[]>(() => {
    const lines = this.lines();
    const partial = this.manualDraft();
    if (lines.length < 6 && partial !== undefined) {
      return POSITIONS.map((_, i) => partial[i]);
    }
    return POSITIONS.map((_, i) => lines[i]);
  });

  /**
   * Eén `e-segmented-toggle` per lijn. De optiewaarde is `{waarde}-{lijn}`, zodat
   * de toggle met testId `iching-manual` de knop-id `iching-manual-{waarde}-{lijn}`
   * krijgt; `''` = nog niets gekozen (geen knop actief).
   */
  protected readonly manualRows = computed<ManualRow[]>(() => {
    const values = this.manualValues();
    const legend = this.legend();
    return POSITIONS_TOP_DOWN.map(position => {
      const value = values[position - 1];
      return {
        position,
        value: value === undefined ? '' : `${value}-${position}`,
        options: LINE_VALUES.map(v => ({ value: `${v}-${position}`, label: `${v}`, title: legend?.values[`${v}`] })),
      };
    });
  });

  /** Ingevulde waarden voor het worp-overzicht, ook een onvolledige reeks. */
  protected readonly manualLedgerLines = computed(() => this.manualValues());

  protected setManual(optionValue: string): void {
    const [value, position] = optionValue.split('-').map(Number) as [IChingLineValue, IChingLinePosition];
    const next = this.manualValues().map((v, i) => (i === position - 1 ? value : v));
    this.manualValues.set(next);
    this.manualDraftChange.emit(next);
    if (next.every(v => v !== undefined)) {
      this.linesChange.emit(next as IChingLineValue[]);
    }
  }

  // ── Direct ─────────────────────────────────────────────────────────────

  /** Gekozen hexagram; teruggerekend uit een volledige worp. */
  protected readonly directHexagram = linkedSignal<number | undefined>(() => {
    const lines = this.lines();
    return lines.length === 6 ? linesToHexagram(lines) : undefined;
  });

  protected readonly directMoving = linkedSignal<readonly IChingLinePosition[]>(() => {
    const lines = this.lines();
    return lines.length === 6 ? movingPositions(lines) : [];
  });

  protected pickHexagram(n: number): void {
    if (!Number.isInteger(n) || n < 1 || n > 64) {
      return;
    }
    this.directHexagram.set(n);
    this.emitDirect();
  }

  protected pickNumber(raw: string): void {
    this.pickHexagram(Number(raw));
  }

  protected toggleMoving(position: IChingLinePosition, checked: boolean): void {
    const others = this.directMoving().filter(p => p !== position);
    this.directMoving.set(checked ? [...others, position].sort((a, b) => a - b) : others);
    this.emitDirect();
  }

  private emitDirect(): void {
    const n = this.directHexagram();
    if (n === undefined) {
      return;
    }
    this.linesChange.emit(linesFromHexagram(n, this.directMoving()));
  }
}
